import React from 'react';
import { Box, Card, Typography, Stack, Skeleton } from '@mui/material';
import theme from '../../theme';
import { getAgentCallsMetrics } from '../../utils/apis';
import { useQuery } from '@tanstack/react-query';

interface MetricCardProps {
  value: string;
  label: string;
}

interface AgentCallsMetricsProps {
  agentId: string;
  fromDate: string;
  toDate: string;
}

const MetricCard: React.FC<MetricCardProps & { isLoading?: boolean }> = ({
  value,
  label,
  isLoading,
}) => (
  <Card
    sx={{
      p: '24px',
      borderRadius: '6px',
      boxShadow: 'none',
      border: `1px solid ${theme.palette.lightGray.main}`,
    }}
  >
    {isLoading ? (
      <>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={20} />
      </>
    ) : (
      <>
        <Typography
          variant="h4"
          component="div"
          sx={{ fontWeight: 'bold', mb: 1 }}
        >
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </>
    )}
  </Card>
);

const AgentCallsMetrics: React.FC<AgentCallsMetricsProps> = ({
  agentId,
  fromDate,
  toDate,
}) => {
  const { isLoading, data } = useQuery({
    queryKey: ['agentCallsMetrics', agentId, fromDate, toDate],
    queryFn: () => getAgentCallsMetrics({ agentId, fromDate, toDate }),
    enabled: Boolean(agentId || fromDate || toDate),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 1,
    retry: 0,
    gcTime: 1000 * 60 * 10,
  });

  const agentCallsMetrics = data?.data;

  return (
    <Box
      sx={{
        width: '100%',
        border: `1px solid ${theme.palette.lightGray.main}`,
        borderRadius: '12px',
        padding: '8px',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Stack direction="row" spacing={3}>
        <Box flex={1}>
          <MetricCard
            value={`${agentCallsMetrics?.totalTime} min.`}
            label="Total Call Time"
            isLoading={isLoading}
          />
        </Box>
        <Box flex={1}>
          <MetricCard
            value={`${agentCallsMetrics?.totalCalls} calls`}
            label="Number Of Calls"
            isLoading={isLoading}
          />
        </Box>
        <Box flex={1}>
          <MetricCard
            value={`$${agentCallsMetrics?.totalCost}`}
            label="Total Spent"
            isLoading={isLoading}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default AgentCallsMetrics;
